import React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HelpIcon from '@mui/icons-material/Help';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import type { LandingAgentTurn } from '../../../stores/slices/landingAgentSlice';
import { displayDiagnosis } from '../../agent-common/diagnosisLabels';

/** One live stage in plain words; null for an event that is not a step. */
export function stepOf(evt: any): string | null {
  switch (evt?.type) {
    case 'analysis_started': return 'Started the analysis in the background';
    case 'analysis_progress': return evt.description || 'Querying data';
    case 'mission_planned': {
      const n = (evt.sub_tasks || []).length;
      // nothing planned (the loop plans no steps): no step to say
      if (!n) return null;
      return `Planned ${n} step${n === 1 ? '' : 's'}`;
    }
    case 'sub_task_started':
      return `${evt.description || 'Working on a step'}${evt.attempt && evt.attempt > 1 ? ` (try ${evt.attempt})` : ''}`;
    case 'worker_result':
      return evt.status === 'error' ? 'A query failed'
        : `Ran a query${evt.row_count != null ? ` · ${evt.row_count} row${evt.row_count === 1 ? '' : 's'}` : ''}`;
    case 'gatekeeper_verdict': return `Checked the result${evt.verdict ? `: ${evt.verdict}` : ''}`;
    case 'retry_queued': return 'Retrying a step';
    case 'retry_exhausted': return 'Stopped retrying';
    case 'formatter_result': return 'Formatted the results';
    case 'synthesis_started': {
      const n = evt.data_producing_tasks;
      if (n === 0) return null;
      return typeof n === 'number' ? `Summarizing ${n} result${n === 1 ? '' : 's'}` : 'Summarizing';
    }
    case 'synthesis_result': return 'Wrote the summary';
    case 'clarification_required': return 'Needs your input';
    case 'sub_task_skipped': return `Skipped a step${evt.reason ? `: ${evt.reason}` : ''}`;
    case 'sub_task_error': return `A step failed${evt.error ? `: ${evt.error}` : ''}`;
    case 'mission_error': return `Failed${evt.error ? `: ${evt.error}` : ''}`;
    default: return null;
  }
}

const FAILED = new Set(['sub_task_error', 'mission_error', 'retry_exhausted']);

const ENDED: Record<string, string> = {
  done: 'Done', clarifying: 'Needs your input', defining: 'Needs a definition', error: 'Failed', cancelled: 'Stopped',
};

interface Step { text: string; kind: 'done' | 'failed' | 'asks' }

/** The turn's steps: its live stages, or — for a walk, which logs none — the walk's own steps. */
function stepsOf(turn: LandingAgentTurn): Step[] {
  const live = (turn.events || []).flatMap((e: any): Step[] => {
    const text = stepOf(e);
    if (!text) return [];
    return [{ text, kind: FAILED.has(e.type) ? 'failed' : e.type === 'clarification_required' ? 'asks' : 'done' }];
  });
  if (live.length) return live;
  const diagnose = turn.result?.diagnose;
  const walk: any[] = (diagnose ? displayDiagnosis(diagnose).steps : null) || [];
  return walk.filter((s) => s && s.summary).map((s) => ({ text: String(s.summary), kind: 'done' as const }));
}

function StepIcon({ kind, spinning }: { kind: Step['kind']; spinning: boolean }) {
  if (kind === 'failed') return <ErrorIcon sx={{ fontSize: 15 }} color="error" />;
  if (kind === 'asks') return <HelpIcon sx={{ fontSize: 15 }} color="warning" />;
  if (spinning) return <CircularProgress size={12} />;
  return <CheckCircleIcon sx={{ fontSize: 15 }} color="success" />;
}

/** Whether the turn has any step to list. */
export function hasSteps(turn: LandingAgentTurn): boolean {
  return stepsOf(turn).length > 0;
}

/** The turn's steps as a list (the block's body; F4's "How this was worked out", #1858 §3, opens the same list). */
export function WorkingSteps({ turn }: { turn: LandingAgentTurn }) {
  const running = turn.status === 'running';
  const steps = stepsOf(turn);
  if (!steps.length) return null;
  return (
    <Box component="ol" data-testid="working-steps"
      sx={{ listStyle: 'none', m: 0, mt: 0.75, ml: 1.375, pl: 2.25, py: 0.25, borderLeft: '1px solid var(--line)',
        display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {steps.map((s, i) => (
        <Box component="li" key={i} sx={{ display: 'grid', gridTemplateColumns: '16px minmax(0,1fr)', gap: 1.25,
          alignItems: 'start', fontSize: 13, color: 'var(--ink-2)', py: 0.375 }}>
          <Box sx={{ mt: '2px', display: 'grid', placeItems: 'center' }}>
            <StepIcon kind={s.kind} spinning={running && i === steps.length - 1} />
          </Box>
          <span>{s.text}</span>
        </Box>
      ))}
    </Box>
  );
}

const DOT: Record<Step['kind'], string> = { done: 'var(--acc, var(--accent))', failed: 'var(--bad)', asks: 'var(--warn)' };

/** The turn's steps as a short timeline (the answer panel's Trace tab): a dot per step on one thin line. */
export function StepTimeline({ turn }: { turn: LandingAgentTurn }) {
  const steps = stepsOf(turn);
  if (!steps.length) return null;
  return (
    <Box component="ol" data-testid="working-steps" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column' }}>
      {steps.map((s, i) => (
        <Box component="li" key={i} data-kind={s.kind}
          sx={{ display: 'grid', gridTemplateColumns: '14px minmax(0,1fr)', gap: 1.5, alignItems: 'start', pb: 2, position: 'relative',
            '&:last-of-type': { pb: 0 },
            '&:not(:last-of-type)::before': { content: '""', position: 'absolute', left: '4px', top: '16px', bottom: 0, width: '1px', bgcolor: 'var(--line)' } }}>
          <Box aria-hidden sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: DOT[s.kind], mt: '5px' }} />
          <Box sx={{ fontSize: 13.5, color: 'var(--ink)', minWidth: 0, overflowWrap: 'anywhere' }}>{s.text}</Box>
        </Box>
      ))}
    </Box>
  );
}

/**
 * The mock's "Working" block (#1506, flag on): the turn's live stages, open while it runs and collapsed to a step
 * count once it ends. It renders the events the turn already has (the same ones AgentStatusPanel reads); nothing
 * new is asked of the server.
 */
export default function WorkingBlock({ turn }: { turn: LandingAgentTurn }) {
  const running = turn.status === 'running';
  const [open, setOpen] = React.useState(running);
  React.useEffect(() => { setOpen(running); }, [running]);
  if (turn.status === 'idle') return null;
  const steps = stepsOf(turn);
  // an ended turn that logged nothing has nothing to report (a replayed answer carries no event log)
  if (!running && steps.length === 0) return null;
  const label = running ? 'Working'
    : `${steps.length} step${steps.length === 1 ? '' : 's'} · ${ENDED[turn.status] || 'Done'}`;
  return (
    <Box data-testid="working-block" data-open={open ? 'true' : 'false'} sx={{ display: 'flex', flexDirection: 'column' }}>
      <ButtonBase onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid="working-toggle"
        sx={{ alignSelf: 'flex-start', gap: 1, px: 1, py: 0.375, ml: -0.5, borderRadius: '7px', fontSize: 13.5,
          color: 'var(--ink-3)', '&:hover': { bgcolor: 'var(--hover, var(--surface2))', color: 'var(--ink-2)' } }}>
        {running ? <CircularProgress size={13} /> : <CheckCircleIcon sx={{ fontSize: 15 }} color={turn.status === 'error' ? 'error' : 'success'} />}
        <span data-testid="working-label">{label}</span>
        <ChevronRightIcon sx={{ fontSize: 16, transition: 'transform .15s', transform: open ? 'rotate(90deg)' : 'none' }} />
      </ButtonBase>
      {open && <WorkingSteps turn={turn} />}
    </Box>
  );
}
